import React, { useMemo } from 'react';
import * as mimeTypes from 'mime-types';
import { getDefaultIconByFileType } from "../../common/utility";
import { EMPTY_TEXT } from "../../common/constants";
import { RectangleTagInnerContainer, RectangleTagOuterContainer, StyledEditIcon, StyledNamedTileView, StyledTagIcon, StyledTagLine, StyledTagNamedTileText, StyledTagText, StyledTagsWrapper, StyledThumbnailContainerNamedTile } from './style';
import { Thumbnail } from '../thumbnail';
import { TagType, TileOrientation } from './enums';
import { ELIPSIS, ELLIPSIS_BUFFER_TEXT, EXTENSION_OFFSET, HORIZONTAL_TILE_WIDTH, MAX_DISPLAY_LENGTH_TEXT, MAX_SINGLE_LINE_FILENAME, MAX_TOTAL_LENGTH_FILENAME, MIN_TWO_LINE_FILENAME, THRESHOLD_FOR_DOT_SPLIT_FILENAME, TILE_DIMENSION_ADJUSTMENT, TRUNCATE_MIDDLE_END, VERTICAL_TEXT_MAX_LENGTH_MULTISPACE, VERTICAL_TEXT_MAX_LENGTH_SINGLESPACE, VERTICAL_TEXT_MULTIWORD_MAX_LENGTH, VERTICAL_TEXT_SPACE_THRESHOLD, VERTICAL_TEXT_SHORT_WORD_LENGTH_THRESHOLD } from './constants';
import Magnetic from '@mitchell/scss.magnetic';
export const MultiTaggedTile = (props) => {
    const isHorizontal = props.orientation === TileOrientation.HORIZONTAL;
    const handleTileSelect = (e) => {
        if (props.shouldNotSelectText) {
            // Prevent text selection when shouldNotSelectText is enabled.
            e.preventDefault();
        }
        if (props.onTileClick) {
            const selectedStateForCallback = props.isSelectModeOn
                ? !props.isSelected
                : props.isSelected;
            props.onTileClick(props.id, selectedStateForCallback);
        }
    };
    const handleEditIconClick = (e) => {
        e.stopPropagation();
        if (props.onEditIconClick) {
            props.onEditIconClick(props.id);
        }
    };
    /**Parses a unit of measurement and adds a value to it.
     * Returns the result as a string of that same type of unit.
     */
    const addToMeasurement = (measure, amountToAdd) => {
        var measureAsNumber = parseMeasurement(measure);
        measureAsNumber += amountToAdd;
        if (measure.includes('px')) {
            return measureAsNumber + 'px';
        }
        if (measure.includes('rem')) {
            return measureAsNumber + 'rem';
        }
        if (measure.includes('em')) {
            return measureAsNumber + 'em';
        }
        // If we couldn't parse the units, let the original measurement pass through.
        return measure;
    };
    /**Parses a unit of measurement.
     * Returns the result as a number.
     */
    const parseMeasurement = (measure) => {
        const measureAsNumber = Number.parseFloat(measure);
        if (Number.isNaN(measureAsNumber)) {
            return 0;
        }
        return measureAsNumber;
    };
    // Process filename tags (similar to the original NamedTile logic)
    const processFileName = (fileName) => {
        const length = fileName.length;
        const indexOfDot = fileName.indexOf('.');
        if (length <= MAX_SINGLE_LINE_FILENAME)
            return [fileName];
        if (indexOfDot > 0) {
            if (length >= MIN_TWO_LINE_FILENAME && length <= THRESHOLD_FOR_DOT_SPLIT_FILENAME) {
                return [fileName.slice(0, indexOfDot), fileName.slice(indexOfDot)];
            }
            if (length > MAX_TOTAL_LENGTH_FILENAME) {
                const secondLine = fileName.slice(MAX_SINGLE_LINE_FILENAME, TRUNCATE_MIDDLE_END) +
                    '...' +
                    fileName.slice(indexOfDot - EXTENSION_OFFSET);
                return [fileName.slice(0, MAX_SINGLE_LINE_FILENAME), secondLine];
            }
        }
        // Default case for long text without extension or between 19-30 chars
        return [
            fileName.slice(0, MAX_SINGLE_LINE_FILENAME),
            fileName.slice(MAX_SINGLE_LINE_FILENAME, Math.min(length, MAX_TOTAL_LENGTH_FILENAME))
        ];
    };
    // Process regular text tags
    const processText = (text) => {
        if (!isHorizontal) {
            const spaceCount = (text.match(/\s/g) || []).length;
            if (spaceCount > VERTICAL_TEXT_SPACE_THRESHOLD &&
                text.length > VERTICAL_TEXT_MAX_LENGTH_MULTISPACE) {
                return [text.substring(0, VERTICAL_TEXT_MAX_LENGTH_MULTISPACE) + ELIPSIS];
            }
            if (spaceCount <= VERTICAL_TEXT_SPACE_THRESHOLD &&
                text.length > VERTICAL_TEXT_MAX_LENGTH_SINGLESPACE) {
                return [text.substring(0, VERTICAL_TEXT_MAX_LENGTH_SINGLESPACE) + ELIPSIS];
            }
            return [text];
        }
        if (text.length <= MAX_DISPLAY_LENGTH_TEXT) {
            return [text];
        }
        // For longer text, break into multiple lines as needed
        const lines = [];
        let remaining = text;
        while (remaining.length > 0) {
            // Find a good break point (space) if possible
            let breakPoint = remaining.lastIndexOf(' ', MAX_DISPLAY_LENGTH_TEXT);
            if (breakPoint <= 0) {
                breakPoint = MAX_DISPLAY_LENGTH_TEXT;
            }
            lines.push(remaining.substring(0, breakPoint).trim());
            remaining = remaining.substring(breakPoint).trim();
            // Limit to 2 lines max
            if (lines.length === 1 && remaining.length > 0) {
                if (remaining.length > MAX_DISPLAY_LENGTH_TEXT) {
                    lines.push(remaining.substring(0, MAX_DISPLAY_LENGTH_TEXT - ELLIPSIS_BUFFER_TEXT) + '...');
                }
                else {
                    lines.push(remaining);
                }
                break;
            }
        }
        return lines;
    };
    const processedTags = useMemo(() => {
        return props.tags.map(tag => {
            const text = tag.text || EMPTY_TEXT;
            if (tag.type === TagType.FILE_NAME) {
                return {
                    type: tag.type,
                    lines: processFileName(text),
                    icon: tag.icon,
                    iconColor: tag.iconColor,
                    needsEllipsis: false,
                    wrapsToNextLine: false // Explicitly set for FILE_NAME tags
                };
            }
            else {
                // For regular text, treat as a single string for ellipsis
                const lines = processText(text);
                const normalizedText = text.trim();
                const wordsInText = normalizedText.split(/\s+/);
                const hasMultipleWords = wordsInText.length > 1;
                const wrapsToNextLine = (!isHorizontal &&
                    hasMultipleWords &&
                    normalizedText.length > VERTICAL_TEXT_MULTIWORD_MAX_LENGTH) ||
                    (wordsInText[0].length < VERTICAL_TEXT_SHORT_WORD_LENGTH_THRESHOLD &&
                        normalizedText.length === VERTICAL_TEXT_MULTIWORD_MAX_LENGTH);
                return {
                    type: tag.type,
                    text,
                    icon: tag.icon,
                    iconColor: tag.iconColor,
                    needsEllipsis: lines.length > 1 || (lines[0] || '').endsWith('...'),
                    wrapsToNextLine
                };
            }
        });
    }, [props.tags, isHorizontal]);
    const additionalHeight = useMemo(() => {
        if (processedTags.length === 0) {
            return 10;
        }
        // Only count lines for file name tags, 1 for text tags
        return (processedTags.reduce((sum, tag) => {
            if (tag.type === TagType.FILE_NAME) {
                return sum + tag.lines.length;
            }
            return sum + 1;
        }, 0) * 20);
    }, [processedTags]);
    const hasAnyEllipsis = processedTags.some(tag => tag.needsEllipsis || tag.wrapsToNextLine);
    const { tileHeight, tileWidth } = useMemo(() => ({
        tileHeight: addToMeasurement(props.height, isHorizontal ? TILE_DIMENSION_ADJUSTMENT : additionalHeight),
        tileWidth: isHorizontal
            ? HORIZONTAL_TILE_WIDTH
            : addToMeasurement(props.width, TILE_DIMENSION_ADJUSTMENT)
    }), [props.height, props.width, isHorizontal, additionalHeight]);
    // Apply marginBottom for vertical orientation with ellipsis or wrapped tag text.
    const extraBottomStyle = !isHorizontal && hasAnyEllipsis ? { marginBottom: Magnetic.spacer16 } : {};
    return (React.createElement(StyledNamedTileView, { id: `multi-tagged-tile-${props.index}`, height: tileHeight, width: tileWidth, isHorizontal: isHorizontal, style: Object.assign(Object.assign({}, props.style), extraBottomStyle) },
        React.createElement(StyledThumbnailContainerNamedTile, { selectMode: props.isSelectModeOn, selected: props.isSelected, warningIconSelected: !!props.warningTypeIcon },
            React.createElement(Thumbnail, { id: props.index, onClick: handleTileSelect, url: props.url, height: props.height, width: props.width, defaultIcon: props.defaultIcon ||
                    getDefaultIconByFileType(mimeTypes.lookup(props.fileName), props.shouldShowThumbnailForPdf, props.url), isPrimaryImage: props.isPrimaryImage, attachmentTypeIcon: props.attachmentTypeIcon, warningTypeIcon: props.warningTypeIcon })),
        React.createElement(StyledTagsWrapper, { isHorizontal: isHorizontal }, processedTags.map((tag, tagIndex) => (React.createElement(RectangleTagOuterContainer, { key: `tag-${props.index}-${tagIndex}`, id: `rect-tile-tag-${props.index}-${tagIndex}`, hasMarginTop: tagIndex > 0 },
            React.createElement(RectangleTagInnerContainer, { selected: !!props.isSelected, shouldNotSelectText: !!props.shouldNotSelectText }, tag.type === TagType.FILE_NAME ? (React.createElement(StyledTagNamedTileText, { isSecondaryTag: !!(tagIndex && isHorizontal) }, Array.isArray(tag.lines) &&
                tag.lines.map((line, lineIdx) => (React.createElement(StyledTagLine, { key: lineIdx },
                    tag.icon && (React.createElement(StyledTagIcon, { className: tag.icon, iconColor: tag.iconColor, id: `tag-icon-${props.index}-${tagIndex}-${lineIdx}` })),
                    React.createElement(StyledTagText, { hasIcon: !!tag.icon }, line)))))) : (React.createElement(StyledTagNamedTileText, Object.assign({ isSecondaryTag: !!(tagIndex && isHorizontal), twoLineEllipsis: !isHorizontal && tag.needsEllipsis }, (tag.type === TagType.CONFIDENCE ? { color: Magnetic.colorMocha60 } : {})),
                tag.icon && (React.createElement(StyledTagIcon, { className: tag.icon, iconColor: tag.iconColor, id: `tag-icon-${props.index}-${tagIndex}-0` })),
                React.createElement(StyledTagText, { hasIcon: !!tag.icon }, tag.text),
                tag.type === TagType.ALLOW_EDIT && (React.createElement(StyledEditIcon, { className: "mi-icon-edit", onClick: handleEditIconClick, role: "button", "aria-label": "Edit" })))))))))));
};
// Set default props
MultiTaggedTile.defaultProps = {
    shouldNotSelectText: true,
    orientation: TileOrientation.VERTICAL
};
//# sourceMappingURL=MultiTaggedTile.js.map